/**
 * EntityNetworkPanel — agent-rendered, read-only view of the case entity
 * ownership network in the center panel.
 *
 * Livi renders this (the render_entity_network tool) right after editing the
 * ownership structure from the case interview — add_case_entity,
 * set_entity_ownership, set_entity_fiduciaries — so the user sees the updated
 * picture. It draws the SAME cytoscape graph as the Entities & Ownership page
 * (EntityNetworkGraph), minus the editing affordances. Clicking a node shows a
 * compact detail strip (name, kind, case role, trade names).
 *
 * The render payload is a point-in-time SNAPSHOT. To stay current when Livi adds
 * an entity or edits ownership AFTER rendering (add_case_entity /
 * set_entity_ownership / set_entity_fiduciaries / merge_entities), the panel
 * polls the live network and prefers it — mirroring how TopicDetailPanel polls
 * useTopicDetail. The payload props are only the first-paint fallback (and the
 * shape is identical: both come from ownership.get_entity_network).
 *
 * Props (from the agent's render_entity_network payload):
 *   entities: { id, kind, display_name, case_role, dbas, ... }[]
 *   edges:    { owner_id, owned_id, pct, basis }[]
 *   onInteraction?: (event) => void   — unused; this panel is read-only.
 */

import React, { useMemo, useState } from 'react';
import { Card, SectionTitle, Badge } from '../../design-system';
import EntityNetworkGraph from '../ui/EntityNetworkGraph';
import { useGetEntityNetwork } from '../../api/entities';
import styles from './EntityNetworkPanel.module.css';

// Match TopicDetailPanel's live-poll cadence so a mutation Livi makes shows up
// within a few seconds without the user reopening the panel.
const NETWORK_REFRESH_MS = 7000;

// Canonical kind order for the legend. Swatch colors mirror the graph nodes and
// the Entities & Ownership list chips so a type reads identically everywhere.
const KIND_ORDER = ['Person', 'Business', 'Trust', 'Estate'];
const KIND_CLASS = { Person: 'kindPerson', Business: 'kindBusiness', Trust: 'kindTrust', Estate: 'kindEstate' };

function KindChip({ kind }) {
  return <span className={`${styles.kindChip} ${styles[KIND_CLASS[kind]] || ''}`}>{kind}</span>;
}

export default function EntityNetworkPanel({ entities: payloadEntities = [], edges: payloadEdges = [] }) {
  // Poll the live network; prefer it over the render-payload snapshot so entities
  // Livi adds after rendering appear. The payload seeds the first paint.
  const { data: liveData } = useGetEntityNetwork(true, {
    refreshInterval: NETWORK_REFRESH_MS,
    fallbackData: { entities: payloadEntities, edges: payloadEdges },
  });
  const rawEntities = liveData?.entities ?? payloadEntities;
  const rawEdges = liveData?.edges ?? payloadEdges;

  // SWR returns a fresh array reference on every poll. Derive a content
  // signature and only hand NEW references to the cytoscape graph when the
  // network actually changed — otherwise EntityNetworkGraph (which rebuilds on
  // [entities, edges] identity) would re-layout and flicker every poll.
  const signature = useMemo(() => JSON.stringify([
    rawEntities.map((e) => [e.id, e.display_name, e.kind, e.case_role, e.hide_from_chart, (e.dbas || []).join(',')]),
    rawEdges.map((ed) => [ed.owner_id, ed.owned_id, ed.pct]),
  ]), [rawEntities, rawEdges]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const entities = useMemo(() => rawEntities, [signature]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const edges = useMemo(() => rawEdges, [signature]);

  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(
    () => entities.find((e) => e.id === selectedId) || null,
    [entities, selectedId],
  );

  // Legend shows only the kinds actually present, in canonical order.
  const presentKinds = useMemo(() => {
    const set = new Set(entities.map((e) => e.kind));
    return KIND_ORDER.filter((k) => set.has(k));
  }, [entities]);

  const entityLabel = entities.length === 1 ? 'entity' : 'entities';
  const linkLabel = edges.length === 1 ? 'link' : 'links';
  const subtitle = `${entities.length} ${entityLabel} · ${edges.length} ownership ${linkLabel}`;

  return (
    <div className={styles.panel}>
      <SectionTitle title="Entity Ownership" />
      <Card padding="md">
        <div className={styles.meta}>
          <span className={styles.subtitle}>{subtitle}</span>
          {presentKinds.length > 0 && (
            <div className={styles.legend}>
              {presentKinds.map((k) => <KindChip key={k} kind={k} />)}
            </div>
          )}
        </div>

        <EntityNetworkGraph
          entities={entities}
          edges={edges}
          selectedId={selectedId}
          onSelectEntity={setSelectedId}
        />

        {selected && (
          <div className={styles.selected}>
            <KindChip kind={selected.kind} />
            <span className={styles.selectedName}>{selected.display_name || '(unnamed)'}</span>
            {selected.case_role && (
              <Badge variant="info" size="sm">{selected.case_role}</Badge>
            )}
            {Array.isArray(selected.dbas) && selected.dbas.length > 0 && (
              <span className={styles.dbas}>dba {selected.dbas.join(', ')}</span>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
